import bech32 from "bech32";
import { createEIP712, generateTypes } from "@tharsis/eip712";
import * as staking from "@tharsis/proto/dist/proto/cosmos/staking/v1beta1/tx";
import * as gov from "@tharsis/proto/dist/proto/cosmos/gov/v1beta1/tx";
import * as distribution from "@tharsis/proto/dist/proto/cosmos/distribution/v1beta1/tx";
import * as bank from "@tharsis/proto/dist/proto/cosmos/bank/v1beta1/tx";
import * as coin from "@tharsis/proto/dist/proto/cosmos/base/v1beta1/coin";
import { normalizeRawTx } from "./tendermint";
import { createTransaction } from "../lib/protoTx";

const msgsMap = {
  "cosmos-sdk/MsgSend": {
    path: "cosmos.bank.v1beta1.MsgSend",
    New: bank.cosmos.bank.v1beta1.MsgSend,
  },
  "cosmos-sdk/MsgDelegate": {
    path: "cosmos.staking.v1beta1.MsgDelegate",
    New: staking.cosmos.staking.v1beta1.MsgDelegate,
  },
  "cosmos-sdk/MsgUndelegate": {
    path: "cosmos.staking.v1beta1.MsgUndelegate",
    New: staking.cosmos.staking.v1beta1.MsgUndelegate,
  },
  "cosmos-sdk/MsgBeginRedelegate": {
    path: "cosmos.staking.v1beta1.MsgBeginRedelegate",
    New: staking.cosmos.staking.v1beta1.MsgBeginRedelegate,
  },
  "cosmos-sdk/MsgWithdrawDelegationReward": {
    path: "cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward",
    New: distribution.cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward,
  },
  "cosmos-sdk/MsgWithdrawDelegatorReward": {
    path: "cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward",
    New: distribution.cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward,
  },
  "cosmos-sdk/MsgVote": {
    path: "cosmos.gov.v1beta1.MsgVote",
    New: gov.cosmos.gov.v1beta1.MsgVote,
  },
  "cosmos-sdk/MsgDeposit": {
    path: "cosmos.gov.v1beta1.MsgDeposit",
    New: gov.cosmos.gov.v1beta1.MsgDeposit,
  },
};

const defaultTypes = {
  ["cosmos-sdk/MsgSend"]: {
    MsgValue: [
      { name: "from_address", type: "string" },
      { name: "to_address", type: "string" },
      { name: "amount", type: "TypeAmount[]" },
    ],
    TypeAmount: [
      { name: "denom", type: "string" },
      { name: "amount", type: "string" },
    ],
  },
  ["cosmos-sdk/MsgDelegate"]: {
    MsgValue: [
      { name: "delegator_address", type: "string" },
      { name: "validator_address", type: "string" },
      { name: "amount", type: "TypeAmount" },
    ],
    TypeAmount: [
      { name: "denom", type: "string" },
      { name: "amount", type: "string" },
    ],
  },
  ["cosmos-sdk/MsgUndelegate"]: {
    MsgValue: [
      { name: "delegator_address", type: "string" },
      { name: "validator_address", type: "string" },
      { name: "amount", type: "TypeAmount" },
    ],
    TypeAmount: [
      { name: "denom", type: "string" },
      { name: "amount", type: "string" },
    ],
  },
  ["cosmos-sdk/MsgBeginRedelegate"]: {
    MsgValue: [
      { name: "delegator_address", type: "string" },
      { name: "validator_src_address", type: "string" },
      { name: "validator_dst_address", type: "string" },
      { name: "amount", type: "TypeAmount" },
    ],
    TypeAmount: [
      { name: "denom", type: "string" },
      { name: "amount", type: "string" },
    ],
  },
  ["cosmos-sdk/MsgWithdrawDelegationReward"]: {
    MsgValue: [
      { name: "delegator_address", type: "string" },
      { name: "validator_address", type: "string" },
    ],
  },
  ["cosmos-sdk/MsgVote"]: {
    MsgValue: [
      { name: "proposal_id", type: "uint64" },
      { name: "voter", type: "string" },
      { name: "option", type: "int32" },
    ],
  },
  ["cosmos-sdk/MsgDeposit"]: {
    MsgValue: [
      { name: "proposal_id", type: "string" },
      { name: "depositer", type: "string" },
      { name: "amount", type: "TypeAmount" },
    ],
    TypeAmount: [
      { name: "denom", type: "string" },
      { name: "amount", type: "string" },
    ],
  },
};

const TxTypesWithTimeout = [
  { name: "account_number", type: "string" },
  { name: "chain_id", type: "string" },
  { name: "fee", type: "Fee" },
  { name: "memo", type: "string" },
  { name: "msgs", type: "Msg[]" },
  { name: "sequence", type: "string" },
  { name: "timeout_height", type: "string" },
];

const TxTypesFee = [
  { name: "amount", type: "Coin[]" },
  { name: "gas", type: "string" },
];

export const getEipToSign = (rawTx, { ethChainId, address }) => {
  const msgTypes = defaultTypes[rawTx.msgs[0].type];
  const types = generateTypes(msgTypes);
  const fee = {
    ...rawTx.fee,
    feePayer: address,
  };
  return createEIP712(
    types,
    ethChainId,
    normalizeRawTx({
      ...rawTx,
      fee,
    })
  );
};

export const getAminoPayload = (rawTx, { pubkey, path }) => {
  const protoMsgs = createProtoMsgs(rawTx.msgs);
  const tx = createTransaction(
    protoMsgs,
    rawTx.memo || "",
    rawTx.fee.amount[0].amount,
    rawTx.fee.amount[0].denom,
    Number(rawTx.fee.gas),
    "ethsecp256",
    pubkey,
    path,
    Number(rawTx.sequence),
    Number(rawTx.account_number),
    rawTx.chain_id
  );
  return tx.legacyAmino;
};

const createProtoMsgs = (msgs) => {
  return msgs.map((msg) => ({
    path: msgsMap[msg.type].path,
    message: new msgsMap[msg.type].New({
      ...msg.value,
      amount: msg.value.amount
        ? new coin.cosmos.base.v1beta1.Coin(msg.value.amount)
        : undefined,
    }),
  }));
};

export const cosmosToEthAddress = (address) => {
  return `0x${Buffer.from(
    bech32.fromWords(bech32.decode(address).words)
  ).toString("hex")}`;
};

export const ethToCosmosAddress = (address, prefix) => {
  const addressBuffer = Buffer.from(address.slice(2), "hex");
  return bech32.encode(prefix, bech32.toWords(addressBuffer));
};
